import React, { useState } from 'react';
import { Send, X, Image as ImageIcon, AlertTriangle, Lock } from 'lucide-react';
import { useForumComment } from '../../hooks/useForumComment';
import { useProfile } from '../../hooks/useProfile';
import { useAuth } from '../../hooks/useAuth';

interface CommentFormProps {
  topicId: string;
  replyTo?: string | null;
  onCancel?: () => void;
  onSuccess?: (comment: any) => void;
}

const CommentForm: React.FC<CommentFormProps> = ({
  topicId,
  replyTo,
  onCancel,
  onSuccess
}) => {
  const [content, setContent] = useState('');
  const [showImageInput, setShowImageInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const { submitComment, isLoading, error } = useForumComment();
  const { profile } = useProfile();
  const { user, signIn } = useAuth();

  const handleImageInsert = () => {
    if (!imageUrl.trim()) return;

    // Validate URL
    try {
      new URL(imageUrl);
    } catch {
      return;
    }

    // Insert image markdown at cursor or end
    const imageMarkdown = `\n![Image](${imageUrl})\n`;
    setContent(prev => prev + imageMarkdown);
    setImageUrl('');
    setShowImageInput(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const comment = await submitComment(topicId, content, replyTo || undefined);
      // Add user profile data to the new comment
      const commentWithProfile = {
        ...comment,
        user: {
          display_name: profile?.display_name || 'Anonymous User',
          avatar_url: profile?.avatar_url
        }
      };
      setContent('');
      onSuccess?.(commentWithProfile);
      onCancel?.();
    } catch (err) {
      console.error('Failed to submit comment:', err);
    }
  };

  if (!user) {
    return (
      <div className="bg-background-secondary border border-gray-800 rounded-lg p-6 text-center">
        <Lock className="w-8 h-8 text-brand mx-auto mb-3" />
        <h3 className="text-lg font-semibold mb-2">Sign In to Join the Discussion</h3>
        <p className="text-content-secondary mb-4">
          Share your thoughts and contribute to the conversation by signing in.
        </p>
        <button
          onClick={signIn}
          className="btn-primary"
        >
          Sign In to Comment
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Error Messages */}
      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      <div className="relative">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={replyTo ? 'Write a reply...' : 'Write a comment...'}
          className="w-full p-4 bg-background border border-gray-800 rounded-lg resize-none focus:ring-2 focus:ring-brand focus:border-brand"
          rows={4}
          disabled={isLoading}
        />
      </div>

      {/* Image URL Input */}
      {showImageInput && (
        <div className="flex gap-2">
          <input
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="Paste image URL here..."
            className="flex-1 px-4 py-2 bg-background border border-gray-800 rounded-lg"
          />
          <button
            type="button"
            onClick={handleImageInsert}
            className="px-4 py-2 bg-brand hover:bg-brand-dark text-white rounded-lg"
          >
            Insert
          </button>
          <button
            type="button"
            onClick={() => {
              setShowImageInput(false);
              setImageUrl('');
            }}
            className="px-4 py-2 text-content-secondary hover:text-content-primary"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={() => setShowImageInput(true)}
          className="flex items-center gap-2 text-sm text-content-secondary hover:text-content-primary"
          disabled={isLoading}
        >
          <ImageIcon className="w-4 h-4" />
          Add Image
        </button>
        
        <div className="flex gap-2">
          {replyTo && (
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center px-4 py-2 text-content-secondary hover:text-content-primary"
              disabled={isLoading}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="flex items-center px-4 py-2 bg-brand hover:bg-brand-dark text-white rounded-lg disabled:opacity-50"
            disabled={isLoading || !content.trim()}
          >
            <Send className="w-4 h-4 mr-2" />
            {isLoading ? 'Sending...' : replyTo ? 'Reply' : 'Comment'}
          </button>
        </div>
      </div>

      {/* Help Text */}
      {showImageInput && (
        <p className="text-sm text-content-secondary">
          Tip: Upload your image to an image hosting service like Imgur or Postimages first, then paste the direct image URL here.
        </p>
      )}
    </form>
  );
};

export default CommentForm;